// template.component.ts
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  ViewChild
} from '@angular/core';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {take, takeUntil, filter, switchMap, map} from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// Services
import {Constraint, UserStoryService} from '../../../shared/templateGen/template-api.service';
import { StatoSessioneService } from '../../../shared/services/stato-sessione.service';
import { SessionPersistenceService } from '../session-manager/session-persistence.service';

// Interfaces
import {
  AcceptanceCriterion,
  AnalysisContent,
  SupportiveAnalysisContent,
  DataParametersResponse,
  StoryDescriptionResponse,
  TemplateGroupInfo,
  TokenCount
} from '../../../shared/templateGen/templateInterfaces';
import { SupportFile } from '../story/story.component';
import { MacroScenarioItem } from './three-column-workspace/three-column-workspace.component';

export interface CriteriaGroup {
  group_info: TemplateGroupInfo;
  acceptance_criteria: AcceptanceCriterion[];
}

@Component({
  selector: 'app-template',
  templateUrl: './template.component.html',
  styleUrls: ['./template.component.scss']
})
export class TemplateComponent implements OnInit, OnDestroy {
  @ViewChild('testDataEditModal') testDataEditModal: any;
  @ViewChild('newCriterionModal') newCriterionModal: any;
  @ViewChild('editModal') editModal: any;
  @ViewChild('editBackgroundModal') editBackgroundModal: any;
  @ViewChild('newGroupModal') newGroupModal: any;

  @Output() generateTestsEvent = new EventEmitter<string>();

  // Aggiungi queste:
  loadingMessage: string = 'Initializing...';
  loadingProgress: number = 0;

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription = new Subscription();
  private generateTemplateSubscription?: Subscription;
  private generateAnalysisSubscription: Subscription | null = null;

  // Template/AC specific state
  criteriaGroups: CriteriaGroup[] = [];
  storyDescription?: StoryDescriptionResponse;
  dataParameters?: DataParametersResponse;
  extractedConstraints: Constraint[] = [];

  // General state
  refinedStoryString = '';
  selectedModel = '';
  projectLanguage = 'en';
  supportFiles: SupportFile[] = [];
  projectId: string | null = null;

  // Analysis state (moved from ScenarioGeneratorComponent)
  coreAnalysis: AnalysisContent | null = null;
  requirementsAnalysis: AnalysisContent | null = null;
  supportiveAnalysis: SupportiveAnalysisContent | null = null;

  // UI state (moved from ScenarioGeneratorComponent)
  isDebugVisible = false;
  isLoading = false;
  error = '';
  lookForAmbiguitiesInAnalysis = true;

  // Scenario state
  macroScenariosUI: MacroScenarioItem[] = [];

  // Observables for child components
  coreAnalysis$: Observable<AnalysisContent | null>;
  supportiveAnalysis$: Observable<SupportiveAnalysisContent | null>;
  dataParameters$: Observable<DataParametersResponse | undefined>;
  macroScenarios$: Observable<MacroScenarioItem[]>;

  constructor(
    private sessioneService: StatoSessioneService,
    private cd: ChangeDetectorRef,
    private modalService: NgbModal,
    private scenarioService: UserStoryService,
    private sessionService: SessionPersistenceService,
    private toastr: ToastrService
  ) {
    // Initialize observables to pass to child components
    this.coreAnalysis$ = this.sessioneService.coreAnalysis$;
    this.supportiveAnalysis$ = this.sessioneService.supportiveAnalysis$;
    this.dataParameters$ = this.sessioneService.dataParameters$;
    this.macroScenarios$ = this.sessioneService.macroScenarios$;
  }

  ngOnInit(): void {
    // console.log('%c[TemplateComponent] ngOnInit CALLED', 'color: magenta; font-weight: bold;');

    // EXPLICITLY clear all analysis state at component initialization
    this.clearAllAnalysisState();

    // Get project context
    this.projectId = this.sessioneService.projectId.getValue();

    // Subscribe to essential state
    this.subscriptions.add(
      this.sessioneService.refinedStoryString$.pipe(takeUntil(this.destroy$))
        .subscribe(val => this.refinedStoryString = val)
    );
    this.subscriptions.add(
      this.sessioneService.projectLanguage$.pipe(takeUntil(this.destroy$))
        .subscribe(val => this.projectLanguage = val)
    );
    this.subscriptions.add(
      this.sessioneService.supportFiles$.pipe(takeUntil(this.destroy$))
        .subscribe(val => this.supportFiles = val)
    );

    // Subscribe to template/AC state
    this.subscriptions.add(
      this.sessioneService.criteriaGroups$.pipe(takeUntil(this.destroy$))
        .subscribe(groups => this.criteriaGroups = groups)
    );
    this.subscriptions.add(
      this.sessioneService.dataParameters$.pipe(takeUntil(this.destroy$))
        .subscribe(dataParams => this.dataParameters = dataParams)
    );
    this.subscriptions.add(
      this.sessioneService.storyDescription$.pipe(takeUntil(this.destroy$))
        .subscribe(description => this.storyDescription = description)
    );
    this.subscriptions.add(
      this.sessioneService.extractedConstraints$.pipe(takeUntil(this.destroy$))
        .subscribe(constraints => this.extractedConstraints = constraints)
    );

    // Set up analysis subscriptions with explicit clearing
    this.setupAnalysisSubscriptionsWithClearing();

    // Subscribe to macro scenarios for display state
    this.subscriptions.add(
      this.sessioneService.macroScenarios$.pipe(takeUntil(this.destroy$))
        .subscribe((scenarios) => {
          if (scenarios !== undefined) {
            this.macroScenariosUI = scenarios ? [...scenarios] : [];
            this.cd.detectChanges();
          }
        })
    );

    // Initialize with any existing data AFTER subscriptions are set up
    this.initializeAnalysisState();
  }

  private clearAllAnalysisState(): void {
    // console.log('%c[TemplateComponent] clearAllAnalysisState - Explicitly clearing ALL analysis state', 'color: red; font-weight: bold;');

    this.coreAnalysis = null;
    this.requirementsAnalysis = null;
    this.supportiveAnalysis = null;
    this.isDebugVisible = false;
    this.error = '';
    this.isLoading = false;
    this.macroScenariosUI = [];
    // Don't clear these here as they come from other subscriptions:
    // this.criteriaGroups = [];
    // this.storyDescription = undefined;
    // this.dataParameters = undefined;

    // console.log('%c[TemplateComponent] All analysis state explicitly cleared', 'color: green; font-weight: bold;');
    this.cd.detectChanges();
  }

  private setupAnalysisSubscriptionsWithClearing(): void {
    // console.log('%c[TemplateComponent] setupAnalysisSubscriptionsWithClearing CALLED', 'color: blue; font-weight: bold;');

    // Subscribe to analysis changes from session service with explicit clearing
    this.subscriptions.add(
      this.sessioneService.coreAnalysis$.pipe(takeUntil(this.destroy$))
        .subscribe(analysis => {
          // console.log('%c[TemplateComponent] coreAnalysis$ subscription triggered:', 'color: orange; font-weight: bold;');
          // console.log('Received analysis:', analysis);
          // console.log('Previous this.coreAnalysis:', this.coreAnalysis);

          this.coreAnalysis = analysis; // This should be null when cleared

          if (!analysis) {
            // If core analysis is null, also clear dependent state
            this.requirementsAnalysis = null;
            this.isDebugVisible = false;
            // console.log('%c[TemplateComponent] Core analysis is null - cleared dependent state', 'color: green;');
          } else {
            this.isDebugVisible = true;
          }

          // console.log('%c[TemplateComponent] Final this.coreAnalysis:', 'color: orange;', this.coreAnalysis);
          this.cd.detectChanges();
        })
    );

    this.subscriptions.add(
      this.sessioneService.requirementsAnalysis$.pipe(takeUntil(this.destroy$))
        .subscribe(analysis => {
          // console.log('%c[TemplateComponent] requirementsAnalysis$ subscription triggered:', 'color: orange; font-weight: bold;');
          // console.log('Received analysis:', analysis);
          // console.log('Previous this.requirementsAnalysis:', this.requirementsAnalysis);

          this.requirementsAnalysis = analysis; // This should be null when cleared

          // console.log('%c[TemplateComponent] Final this.requirementsAnalysis:', 'color: orange;', this.requirementsAnalysis);
          this.cd.detectChanges();
        })
    );

    this.subscriptions.add(
      this.sessioneService.supportiveAnalysis$.pipe(takeUntil(this.destroy$))
        .subscribe(supportive => {
          // console.log('%c[TemplateComponent] supportiveAnalysis$ subscription triggered:', 'color: orange; font-weight: bold;');
          // console.log('Received supportive:', supportive);
          // console.log('Previous this.supportiveAnalysis:', this.supportiveAnalysis);

          this.supportiveAnalysis = supportive; // This should be null when cleared

          // console.log('%c[TemplateComponent] Final this.supportiveAnalysis:', 'color: orange;', this.supportiveAnalysis);
          this.cd.detectChanges();
        })
    );

    // Subscribe to analysis result stream from scenarioService (for generation workflow)
    this.subscriptions.add(
      this.scenarioService.analysisResult$.pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          // console.log('%c[TemplateComponent] scenarioService.analysisResult$ - ONLY handling loading/errors:', 'color: purple; font-weight: bold;');
          // console.log('Received result:', result);

          if (result.error) {
            this.setError(result.error);
          } else {
            this.clearError();
          }

          // console.log('%c[TemplateComponent] scenarioService handled loading/error only - NOT updating coreAnalysis', 'color: purple;');
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.generateTemplateSubscription?.unsubscribe();
    this.generateAnalysisSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.modalService.dismissAll();
  }

  private initializeAnalysisState(): void {
    // console.log('%c[TemplateComponent] initializeAnalysisState CALLED', 'color: purple; font-weight: bold;');

    // Get current analysis state from service
    const currentCore = this.sessioneService.getCoreAnalysis();
    const currentRequirements = this.sessioneService.getRequirementsAnalysis();
    const currentSupportive = this.sessioneService.getSupportiveAnalysis();

    // console.log('Retrieved from service:');
    // console.log('currentCore:', currentCore);
    // console.log('currentRequirements:', currentRequirements);
    // console.log('currentSupportive:', currentSupportive);

    if (currentCore) {
      // console.log('%cSetting coreAnalysis from service data!', 'color: red; font-weight: bold;');
      this.coreAnalysis = currentCore;
      this.isDebugVisible = true;
    } else {
      // console.log('%cNo core analysis found in service', 'color: green;');
    }

    if (currentRequirements) {
      // console.log('%cSetting requirementsAnalysis from service data!', 'color: red; font-weight: bold;');
      this.requirementsAnalysis = currentRequirements;
    } else {
      // console.log('%cNo requirements analysis found in service', 'color: green;');
    }

    if (currentSupportive) {
      // console.log('%cSetting supportiveAnalysis from service data!', 'color: red; font-weight: bold;');
      this.supportiveAnalysis = currentSupportive;
    } else {
      // console.log('%cNo supportive analysis found in service', 'color: green;');
    }
  }

  // === Loading/Error Management ===
  private setLoading(loading: boolean): void {
    if (this.isLoading !== loading) {
      this.isLoading = loading;
      this.cd.detectChanges();
    }
  }

  private setError(errorMessage: string): void {
    this.error = errorMessage;
    if (errorMessage) {
      this.toastr.error(errorMessage, 'Error');
    }
    this.setLoading(false);
    this.cd.detectChanges();
  }

  private clearError(): void {
    this.error = '';
    this.cd.detectChanges();
  }

  // === Analysis Generation Methods (moved from ScenarioGeneratorComponent) ===

  async generateAnalysisOnly(): Promise<void> {
    if (!this.refinedStoryString.trim()) {
      this.setError('Please enter a user story');
      return;
    }

    let proceedWithGeneration = true;

    // Check if any previous analysis data exists
    const dataWillBeDeleted = !!(
      this.coreAnalysis ||
      this.requirementsAnalysis ||
      this.supportiveAnalysis ||
      this.sessioneService.getCoreAnalysis() ||
      this.sessioneService.getRequirementsAnalysis() ||
      this.sessioneService.getSupportiveAnalysis()
    );

    if (dataWillBeDeleted) {
      const resultDialog = await Swal.fire({
        title: 'Are you sure?',
        text: 'Generating new Analysis will clear any existing analysis results and all derived data (scenarios, tests, etc.). This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, generate new analysis!'
      });

      if (!resultDialog.isConfirmed) {
        this.toastr.info('Analysis generation cancelled by user.');
        proceedWithGeneration = false;
      }
    }

    if (!proceedWithGeneration) {
      return;
    }

    // ✅ SINGLE, CLEAN CALL to reset all relevant session state
    this.sessioneService.resetAnalysisAndDerivedData();
    this.toastr.info('Clearing existing analysis, scenarios, and tests.');

    // Cancel any previous analysis subscription
    this.generateAnalysisSubscription?.unsubscribe();
    this.clearError();

    // ✅ LOCK UI & SET INITIAL MESSAGE
    this.setLoading(true);
    this.loadingMessage = 'Analysis generation...'; // 1° Step
    this.loadingProgress = 10;

    // Start core analysis + ambiguities generation
    this.scenarioService.generateAnalysis(
      this.projectId,
      this.sessioneService.getSessionName(),
      this.refinedStoryString,
      this.projectLanguage,
      this.lookForAmbiguitiesInAnalysis
    );

    this.generateAnalysisSubscription = this.scenarioService.analysisResult$
      .pipe(
        filter(result => !result.isLoading && !!result.core_analysis),
        take(1),
        // --- STEP 1: Core Analysis Complete -> Start Constraints ---
        switchMap(coreResult => {

          // ✅ UPDATE UI MESSAGE: CONSTRAINTS
          this.loadingMessage = 'Constraints generation...'; // 2° Step
          this.loadingProgress = 45;
          this.cd.detectChanges(); // Force UI update

          const coreAnalysisText = coreResult.core_analysis?.scenario_analysis || '';

          if (!coreAnalysisText) {
            // Se non c'è testo, salta l'estrazione dei vincoli
            return of({ coreResult, constraintsResponse: null });
          }

          return this.scenarioService.extractConstraints(
            this.sessioneService.getProjectId(),
            this.sessioneService.getSessionName(),
            coreAnalysisText,
            this.projectLanguage
          ).pipe(
            // Combiniamo il risultato dei vincoli con il risultato dell'analisi principale
            map(constraintsResponse => ({ coreResult, constraintsResponse }))
          );
        }),
        // --- STEP 2: Constraints Complete -> Start Requirements ---
        switchMap(({ coreResult, constraintsResponse }) => {
          // Salva i vincoli nello stato
          if (constraintsResponse) {
            this.extractedConstraints = constraintsResponse.constraints;
            this.sessioneService.updateExtractedConstraints(this.extractedConstraints);
          }

          // ✅ UPDATE UI MESSAGE: REQUIREMENTS
          this.loadingMessage = 'Requirement generation...'; // 3° Step
          this.loadingProgress = 80;
          this.cd.detectChanges(); // Force UI update

          const coreAnalysisString = JSON.stringify(coreResult.core_analysis);

          return this.scenarioService.generateRequirementsAnalysis(
            this.sessioneService.getSessionName(),
            this.refinedStoryString,
            this.projectId,
            coreAnalysisString,
            this.projectLanguage
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (requirementsResult) => {
          this.requirementsAnalysis = requirementsResult.requirements_analysis;

          // Aggiorna lo stato della sessione con tutti i dati raccolti
          this.sessioneService.updateAnalysisData(
            this.coreAnalysis, // Questo è già stato aggiornato dal servizio `scenarioService`
            this.requirementsAnalysis,
            null
          );

          this.loadingProgress = 100;
          this.toastr.success('Core analysis, constraints, and requirements generated successfully.');
          this.saveAnalysisCheckpoint();

          // Small delay to let user see 100% before unlocking
          setTimeout(() => {
            this.setLoading(false);
          }, 500);
        },
        error: (error) => {
          this.setError(`Analysis process failed: ${error.message || 'Unknown error'}`);
          console.error('Error during analysis orchestration:', error);
          this.setLoading(false); // Unlock on error
        },
        complete: () => {
          this.generateAnalysisSubscription = null;
        }
      });
  }

  async generateRequirementsAnalysisOnly(): Promise<void> {
    if (!this.coreAnalysis) {
      this.setError('Core analysis must be generated first');
      return;
    }

    if (!this.refinedStoryString.trim()) {
      this.setError('Please enter a user story');
      return;
    }

    this.generateAnalysisSubscription?.unsubscribe();
    this.setLoading(true);
    this.clearError();

    const coreAnalysisString = JSON.stringify(this.coreAnalysis);

    this.generateAnalysisSubscription = this.scenarioService
      .generateRequirementsAnalysis(
        this.sessioneService.getSessionName(),
        this.refinedStoryString,
        this.projectId,
        coreAnalysisString,
        this.projectLanguage
      )
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          this.requirementsAnalysis = result.requirements_analysis;

          this.sessioneService.updateAnalysisData(
            this.coreAnalysis,
            this.requirementsAnalysis,
            null
          );

          this.toastr.success('Requirements analysis generated successfully');
          this.saveAnalysisCheckpoint();
          this.setLoading(false);
        },
        error: (error) => {
          this.setError(`Requirements analysis error: ${error.message}`);
          console.error('Error generating requirements analysis:', error);
          this.generateAnalysisSubscription = null;
        },
        complete: () => {
          this.generateAnalysisSubscription = null;
        }
      });
  }

  public saveAnalysisChanges(): void {
    if (!this.coreAnalysis) {
      this.toastr.warning('No analysis content available to save.', 'Action Canceled');
      return;
    }

    // Persist the updated analysis objects to the central session service
    this.sessioneService.updateAnalysisData(
      this.coreAnalysis,
      this.requirementsAnalysis,
      this.supportiveAnalysis
    );

    // Call the existing method to save the entire session to the backend.
    // This method already provides user feedback via toastr.
    this.saveAnalysisCheckpoint();
  }

  // === Checkpoint Management ===
  public saveAnalysisCheckpoint(): void {
    this.sessionService.saveCurrentSession('scenarios')
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.success('Progress saved at analysis stage');
        },
        error: (error) => {
          console.error('Error saving analysis checkpoint:', error);
          this.toastr.error('Failed to save checkpoint', '', {timeOut: 3000, positionClass: 'toast-bottom-right'});
        }
      });
  }

  // === Event Handlers for Workspace Component ===
  onWorkspaceDataChanged(): void {
    // console.log('Workspace data changed - could trigger autosave here');
  }

  onWorkspaceCheckpointSaved(sessionName: string): void {
    // console.log(`Workspace checkpoint saved: ${sessionName}`);
  }

  onMacroScenariosGenerated(scenarios: MacroScenarioItem[]): void {
    this.macroScenariosUI = scenarios;
    this.sessioneService.updateMacroScenarios(scenarios);
  }
}
